/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable import/exports-last */
/* eslint-disable consistent-default-export-name/default-export-match-filename */

import { animationRound, coinFlip, degreeToLabel, mapRange, rotate, rotationTransforms } from './utils'
import type { CSSProperties } from 'react'

const ROTATION_SPEED_MIN = 200 // minimum possible duration of single particle full rotation
const ROTATION_SPEED_MAX = 800 // maximum possible duration of single particle full rotation
const CRAZY_PARTICLES_FREQUENCY = 0.8 // 0-1 frequency of crazy curvy unpredictable particles
const CRAZY_PARTICLE_CRAZINESS = 0.33 // 0-1 how crazy these crazy particles are
const BEZIER_MEDIAN = 0.5 // utility for mid-point bezier curves, to ensure smooth motion paths

export type Particle = {
  color: string // color of particle
  degree: number // vector direction, between 0-360 (0 being straight up ↑)
}

export const rotationKeyframeStyles: CSSProperties = rotationTransforms.reduce((acc, xyz, i) => {
  return {
    ...acc,
    [`--confetti-rotation-3d-${i}-50`]: xyz.map((v) => v / 2).join(),
    [`--confetti-rotation-3d-${i}-100`]: xyz.join(),
  }
}, {} as CSSProperties)

export function getConfettiKeyframeStyles(degrees: number[], height: number | string, width: number) {
  const y = typeof height === 'string' ? height : `${height}px`
  const res: Record<string, string> = {
    '--confetti-landing-point-y': y,
  }
  for (const degree of degrees) {
    const x = `${animationRound(mapRange(Math.abs(rotate(degree, 90) - 180), 0, 180, -width / 2, width / 2), 2)}px`
    res[`--confetti-degree-${degreeToLabel(degree)}-landing-point-x`] = x
  }
  return res as CSSProperties
}

export function getConfettoStyle(
  { color, degree }: Particle,
  duration: number,
  force: number,
  size: number,
  isCircle: boolean
): CSSProperties {
  const rotation = Math.round(Math.random() * (ROTATION_SPEED_MAX - ROTATION_SPEED_MIN) + ROTATION_SPEED_MIN)
  const durationChaos = duration - Math.round(Math.random() * 1000)
  const shouldBeCrazy = Math.random() < CRAZY_PARTICLES_FREQUENCY

  // x-axis disturbance, roughly the distance the particle will initially deviate from its target
  const x1 = shouldBeCrazy ? animationRound(Math.random() * CRAZY_PARTICLE_CRAZINESS, 2) : 0
  const x2 = x1 * -1
  const x3 = x1
  // x-axis arc of explosion, so 90deg and 270deg particles have curve of 1, 0deg and 180deg have 0
  const x4 = animationRound(Math.abs(mapRange(Math.abs(rotate(degree, 90) - 180), 0, 180, -1, 1)), 4)

  // roughly how fast particle reaches end of its explosion curve
  const y1 = animationRound(Math.random() * BEZIER_MEDIAN, 4)
  // roughly maps to the distance particle goes before reaching free-fall
  const y2 = animationRound(Math.random() * force * (coinFlip() ? 1 : -1), 4)
  // roughly how soon the particle transitions from explosion to free-fall
  const y3 = BEZIER_MEDIAN
  // roughly the ease of free-fall
  const y4 = animationRound(Math.max(mapRange(Math.abs(degree - 180), 0, 180, force, -force), 0), 4)
  return {
    '--confetti-animation-duration': `${durationChaos}ms`,
    '--confetti-particle-width': `${isCircle ? size : Math.round(Math.random() * 4) + size / 2}px`,
    '--confetti-particle-height': `${isCircle ? size : Math.round(Math.random() * 2) + size}px`,
    '--confetti-particle-color': color,
    '--confetti-rotation-animation-duration': `${rotation}ms`,
    '--confetti-landing-point-x': `var(--confetti-degree-${degreeToLabel(degree)}-landing-point-x)`,
    '--confetti-x1': x1,
    '--confetti-x2': x2,
    '--confetti-x3': x3,
    '--confetti-x4': x4,
    '--confetti-y1': y1,
    '--confetti-y2': y2,
    '--confetti-y3': y3,
    '--confetti-y4': y4,
  } as CSSProperties
}
